import { Route, Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { logoutGuard } from '~ngx-shared/authentication/guards/logout.guard';
import { crudGuard, detailGuard, leaveGuard } from '~ngx-shared/guards';
import { breadcrumbResolver, crudResolver } from '~ngx-shared/resolvers';
import { ApplicationListVariantEnum } from '~shared/models';
import { folderNameBreadcrumbResolver } from '~shared/notification/resolvers/folder-name-breadcrumb.resolver';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';
import { CanAccessPersonDetailGuard } from './guards/can-access-person-detail.guard';
import { CanAccessTeacherFormGuard } from './guards/can-access-teacher-form.guard';
import { notificationResolver } from './resolvers/notification.resolver';

/******************
 * ADDENDUM IGAZAS
 ******************/
const addendumIgazas: Route = {
  path: 'addendum-igazas',
  data: {
    breadcrumb: 'education.addendum_igazas'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/addendum-igaza-list-page/addendum-igaza-list-page.component').then(
          m => m.AddendumIgazaListPageComponent
        )
    },
    {
      path: 'detail/:addendum-igaza-id',
      loadComponent: () =>
        import('./pages/addendum-igaza-detail-page/addendum-igaza-detail-page.component').then(
          m => m.AddendumIgazaDetailPageComponent
        ),
      canActivate: [detailGuard('addendum-igaza-id')],
      resolve: { crud: crudResolver('addendum-igaza-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.addendum_igaza'
      }
    },
    {
      path: ':addendum-igaza-id',
      loadComponent: () =>
        import('./pages/create-update-addendum-igaza-form-page/create-update-addendum-igaza-form-page.component').then(
          m => m.CreateUpdateAddendumIgazaFormPageComponent
        ),
      canActivate: [crudGuard('addendum-igaza-id')],
      resolve: {
        crud: crudResolver('addendum-igaza-id'),
        breadcrumb: breadcrumbResolver('addendum-igaza-id')
      },
      data: {
        breadcrumbParamValue: 'education.addendum_igaza'
      }
    }
  ]
};

/******************
 * ABSENCES
 ******************/
const absences: Route = {
  path: 'absences',
  data: {
    breadcrumb: 'shared.absences'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/absence-list-page/absence-list-page.component').then(
          m => m.AbsenceListPageComponent
        )
    },
    {
      path: 'detail/:absence-id',
      loadComponent: () =>
        import('./pages/absence-detail-page/absence-detail-page.component').then(
          m => m.AbsenceDetailPageComponent
        ),
      canActivate: [detailGuard('absence-id')],
      resolve: { crud: crudResolver('absence-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'shared.absence'
      }
    },
    {
      path: ':absence-id',
      loadComponent: () =>
        import('./pages/create-absence-data-form-page/create-absence-data-form-page.component').then(
          m => m.CreateAbsenceDataFormPageComponent
        ),
      canActivate: [crudGuard('absence-id')],
      resolve: {
        crud: crudResolver('absence-id'),
        breadcrumb: breadcrumbResolver('absence-id')
      },
      data: {
        breadcrumbParamValue: 'shared.absence'
      }
    }
  ]
};

/******************
 * PART TIME JOB
 ******************/
const partTimeJobs: Route = {
  path: 'part-time-jobs',
  data: {
    breadcrumb: 'education.part_time_jobs'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/part-time-job-list-page/part-time-job-list-page.component').then(
          m => m.PartTimeJobListPageComponent
        )
    },
    {
      path: 'detail/:part-time-job-id',
      loadComponent: () =>
        import('./pages/part-time-job-detail-page/part-time-job-detail-page.component').then(
          m => m.PartTimeJobDetailPageComponent
        ),
      canActivate: [detailGuard('part-time-job-id')],
      resolve: { crud: crudResolver('part-time-job-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.part_time_job'
      }
    },
    {
      path: ':part-time-job-id',
      loadComponent: () =>
        import('./pages/create-part-time-job-data-form-page/create-part-time-job-data-form-page.component').then(
          m => m.CreatePartTimeJobDataFormPageComponent
        ),
      canActivate: [crudGuard('part-time-job-id')],
      resolve: {
        crud: crudResolver('part-time-job-id'),
        breadcrumb: breadcrumbResolver('part-time-job-id')
      },
      data: {
        breadcrumbParamValue: 'education.part_time_job'
      }
    }
  ]
};

/******************
 * PROPOSAL HONORS
 ******************/
const proposalHonors: Route = {
  path: 'proposal-honors',
  data: {
    breadcrumb: 'education.proposal_honors'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/proposal-honor-list-page/proposal-honor-list-page.component').then(
          m => m.ProposalHonorListPageComponent
        )
    },
    {
      path: 'detail/:proposal-honor-id',
      loadComponent: () =>
        import('./pages/proposal-honor-detail-page/proposal-honor-detail-page.component').then(
          m => m.ProposalHonorDetailPageComponent
        ),
      canActivate: [detailGuard('proposal-honor-id')],
      resolve: { crud: crudResolver('proposal-honor-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.proposal_honor'
      }
    },
    {
      path: ':proposal-honor-id',
      loadComponent: () =>
        import('./pages/create-update-proposal-honor-form-page/create-update-proposal-honor-form-page.component').then(
          m => m.CreateUpdateProposalHonorFormPageComponent
        ),
      canActivate: [crudGuard('proposal-honor-id')],
      resolve: {
        crud: crudResolver('proposal-honor-id'),
        breadcrumb: breadcrumbResolver('proposal-honor-id')
      },
      data: {
        breadcrumbParamValue: 'education.proposal_honor'
      }
    }
  ]
};

/******************
 * HEARINGS
 ******************/
const hearings: Route = {
  path: 'hearings',
  data: {
    breadcrumb: 'education.hearings'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/hearing-list-page/hearing-list-page.component').then(
          m => m.HearingListPageComponent
        )
    },
    {
      path: 'detail/:hearing-id',
      loadComponent: () =>
        import('./pages/hearing-shell-page/hearing-shell-page.component').then(
          m => m.HearingShellPageComponent
        ),
      canActivate: [detailGuard('hearing-id')],
      resolve: { crud: crudResolver('hearing-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.hearing_application'
      },
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./pages/hearing-detail-page/hearing-detail-page.component').then(
              m => m.HearingDetailPageComponent
            )
        },
        {
          path: 'appointment',
          loadComponent: () =>
            import('./pages/hearing-appointment-page/hearing-appointment-page.component').then(
              m => m.HearingAppointmentPageComponent
            )
        },
        {
          path: 'hearing',
          loadComponent: () =>
            import('./pages/hearing-conversation-page/hearing-conversation-page.component').then(
              m => m.HearingConversationPageComponent
            )
        }
      ]
    },
    {
      path: 'scoring/:case-id/:person-id',
      loadComponent: () =>
        import('./pages/hearing-scoring-form-page/hearing-scoring-form-page.component').then(
          m => m.HearingScoringFormPageComponent
        ),
      canActivate: [ngxPermissionsGuard, detailGuard('case-id'), detailGuard('person-id')],
      data: {
        breadcrumb: 'education.hearing.scoring',
        isReadOnly: true,
        permissions: {
          only: AuthorizationUtil.GROUP_SCHOOL,
          redirectTo: '/'
        }
      }
    },
    {
      path: ':hearing-id',
      loadComponent: () =>
        import('./pages/create-update-hearing-application-form-page/create-update-hearing-application-form-page.component').then(
          m => m.CreateUpdateHearingApplicationFormPageComponent
        ),
      canActivate: [ngxPermissionsGuard, crudGuard('hearing-id')],
      resolve: {
        crud: crudResolver('hearing-id'),
        breadcrumb: breadcrumbResolver('hearing-id')
      },
      data: {
        breadcrumbParamValue: 'education.hearing_application',
        permissions: {
          only: AuthorizationUtil.GROUP_EDUCATION,
          redirectTo: '/'
        }
      }
    }
  ]
};

/******************
 * HEARING SESSIONS
 ******************/
const hearingSessions: Route = {
  path: 'hearing-sessions',
  data: {
    breadcrumb: 'education.hearing_sessions'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/hearing-session-list-page/hearing-session-list-page.component').then(
          m => m.HearingSessionListPageComponent
        )
    },
    {
      path: 'detail/:hearing-session-id',
      loadComponent: () =>
        import('./pages/hearing-session-detail-page/hearing-session-detail-page.component').then(
          m => m.HearingSessionDetailPageComponent
        ),
      canActivate: [detailGuard('hearing-session-id')],
      resolve: { crud: crudResolver('hearing-session-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.hearing_session.session'
      }
    },
    {
      path: ':hearing-session-id',
      loadComponent: () =>
        import('./pages/create-update-hearing-session-form-page/create-update-hearing-session-form-page.component').then(
          m => m.CreateUpdateHearingSessionFormPageComponent
        ),
      canActivate: [ngxPermissionsGuard, crudGuard('hearing-session-id')],
      resolve: {
        crud: crudResolver('hearing-session-id'),
        breadcrumb: breadcrumbResolver('hearing-session-id')
      },
      data: {
        breadcrumbParamValue: 'education.hearing_session.session',
        permissions: {
          only: AuthorizationUtil.GROUP_EDUCATION,
          redirectTo: '/'
        }
      }
    }
  ]
};

/******************
 * HEARING SCORING
 ******************/
const hearingScoring: Route = {
  path: 'hearing-scoring',
  data: {
    breadcrumb: 'education.hearing.my_hearings'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/hearing-scoring-page/hearing-scoring-page.component').then(
          m => m.HearingScoringPageComponent
        )
    },
    {
      path: ':case-id',
      loadComponent: () =>
        import('./pages/hearing-scoring-form-page/hearing-scoring-form-page.component').then(
          m => m.HearingScoringFormPageComponent
        ),
      data: { breadcrumb: 'education.hearing.scoring' }
    }
  ]
};

/******************
 * TRANSFER SERVICES
 ******************/
const transferServices: Route = {
  path: 'transfer-services',
  data: {
    breadcrumb: 'education.transfer_services'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/transfer-service-list-page/transfer-service-list-page.component').then(
          m => m.TransferServiceListPageComponent
        )
    },
    {
      path: 'detail/:transfer-service-id',
      loadComponent: () =>
        import('./pages/transfer-service-detail-page/transfer-service-detail-page.component').then(
          m => m.TransferServiceDetailPageComponent
        ),
      canActivate: [detailGuard('transfer-service-id')],
      resolve: { crud: crudResolver('transfer-service-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.transfer_service'
      }
    },
    {
      path: ':transfer-service-id',
      loadComponent: () =>
        import('./pages/create-update-transfer-service-form-page/create-update-transfer-service-form-page.component').then(
          m => m.CreateUpdateTransferServiceFormPageComponent
        ),
      canActivate: [crudGuard('transfer-service-id')],
      resolve: {
        crud: crudResolver('transfer-service-id'),
        breadcrumb: breadcrumbResolver('transfer-service-id')
      },
      data: {
        breadcrumbParamValue: 'education.transfer_service'
      }
    }
  ]
};

/******************
 * TERMINATION OF SERVICES
 ******************/
const terminationOfServices: Route = {
  path: 'termination-of-services',
  data: {
    breadcrumb: 'shared.termination_of_services'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/termination-of-service-list-page/termination-of-service-list-page.component').then(
          m => m.TerminationOfServiceListPageComponent
        )
    },
    {
      path: 'detail/:termination-of-service-id',
      loadComponent: () =>
        import('~shared/education/pages/termination-of-service-detail-page/termination-of-service-detail-page.component').then(
          m => m.TerminationOfServiceDetailPageComponent
        ),
      canActivate: [detailGuard('termination-of-service-id')],
      resolve: { crud: crudResolver('termination-of-service-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'shared.termination_of_service'
      }
    },
    {
      path: ':termination-of-service-id',
      loadComponent: () =>
        import('./pages/create-update-termination-of-service-form-page/create-update-termination-of-service-form-page.component').then(
          m => m.CreateUpdateTerminationOfServiceFormPageComponent
        ),
      canActivate: [crudGuard('termination-of-service-id', true, false)],
      resolve: {
        crud: crudResolver('termination-of-service-id'),
        breadcrumb: breadcrumbResolver('termination-of-service-id')
      },
      data: {
        breadcrumbParamValue: 'shared.termination_of_service'
      }
    }
  ]
};

/******************
 * TIMETABLES
 ******************/
const timetables: Route = {
  path: 'timetables',
  data: {
    breadcrumb: 'education.timetables'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/timetable-list-page/timetable-list-page.component').then(
          m => m.TimetableListPageComponent
        )
    },
    {
      path: 'operation',
      canActivate: [ngxPermissionsGuard],
      data: {
        breadcrumb: 'education.unit_operation',
        permissions: {
          only: AuthorizationUtil.GROUP_INSPECTOR,
          redirectTo: '/'
        }
      },
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./pages/create-unit-operation-form-page/create-unit-operation-form-page.component').then(
              m => m.CreateUnitOperationFormPageComponent
            )
        },
        {
          path: 'detail/:unit-operation-id',
          loadComponent: () =>
            import('./pages/unit-operation-detail-page/unit-operation-detail-page.component').then(
              m => m.UnitOperationDetailPageComponent
            ),
          canActivate: [detailGuard('unit-operation-id')],
          resolve: { crud: crudResolver('unit-operation-id') },
          data: {
            breadcrumb: 'detail_value',
            breadcrumbParamValue: 'education.unit_operation'
          }
        }
      ]
    },
    {
      path: 'detail/data/:timetable-data-id',
      loadComponent: () =>
        import('./pages/timetable-detail-page/timetable-detail-page.component').then(
          m => m.TimetableDetailPageComponent
        ),
      canActivate: [detailGuard('timetable-data-id')],
      resolve: { crud: crudResolver('timetable-data-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.timetable',
        isTimetableData: true
      }
    },
    {
      path: 'detail/:timetable-id',
      loadComponent: () =>
        import('./pages/timetable-detail-page/timetable-detail-page.component').then(
          m => m.TimetableDetailPageComponent
        ),
      canActivate: [detailGuard('timetable-id')],
      resolve: { crud: crudResolver('timetable-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.timetable'
      }
    },
    {
      path: ':timetable-id',
      loadComponent: () =>
        import('./pages/create-timetable-data-form-page/create-timetable-data-form-page.component').then(
          m => m.CreateTimetableDataFormPageComponent
        ),
      canActivate: [crudGuard('timetable-id')],
      canDeactivate: [leaveGuard()],
      resolve: {
        crud: crudResolver('timetable-id'),
        breadcrumb: breadcrumbResolver('timetable-id')
      },
      data: {
        breadcrumbParamValue: 'education.timetable'
      }
    }
  ]
};

/******************
 * LETTER OF SERVICES
 ******************/
const letterOfServices: Route = {
  path: 'letter-of-services',
  data: {
    breadcrumb: 'education.letter_of_services'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/letter-of-service-list-page/letter-of-service-list-page.component').then(
          m => m.LetterOfServiceListPageComponent
        )
    },
    {
      path: 'detail/:letter-of-service-id',
      loadComponent: () =>
        import('./pages/letter-of-service-detail-page/letter-of-service-detail-page.component').then(
          m => m.LetterOfServiceDetailPageComponent
        ),
      canActivate: [detailGuard('letter-of-service-id')],
      resolve: { crud: crudResolver('letter-of-service-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.letter_of_service'
      }
    },
    {
      path: 'detail/data/:letter-of-service-data-id',
      loadComponent: () =>
        import('./pages/letter-of-service-detail-page/letter-of-service-detail-page.component').then(
          m => m.LetterOfServiceDetailPageComponent
        ),
      canActivate: [detailGuard('letter-of-service-data-id')],
      resolve: { crud: crudResolver('letter-of-service-data-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.letter_of_service',
        isLetterOfServiceData: true
      }
    },
    {
      path: ':letter-of-service-id',
      loadComponent: () =>
        import('./pages/create-letter-of-service-data-form-page/create-letter-of-service-data-form-page.component').then(
          m => m.CreateLetterOfServiceDataFormPageComponent
        ),
      canActivate: [ngxPermissionsGuard, crudGuard('letter-of-service-id')],
      resolve: {
        crud: crudResolver('letter-of-service-id'),
        breadcrumb: breadcrumbResolver('letter-of-service-id')
      },
      data: {
        breadcrumbParamValue: 'education.letter_of_service',
        permissions: {
          only: AuthorizationUtil.GROUP_INSPECTOR,
          redirectTo: '/'
        }
      }
    }
  ]
};

/******************
 * PLANNING FORMS
 ******************/
const planningForms: Route = {
  path: 'planning-forms',
  data: {
    breadcrumb: 'education.planning_forms'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/planning-form-list-page/planning-form-list-page.component').then(
          m => m.PlanningFormListPageComponent
        )
    },
    {
      path: 'detail/:planning-form-id',
      loadComponent: () =>
        import('./pages/planning-form-detail-page/planning-form-detail-page.component').then(
          m => m.PlanningFormDetailPageComponent
        ),
      canActivate: [detailGuard('planning-form-id')],
      resolve: { crud: crudResolver('planning-form-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.planning_form'
      }
    },
    {
      path: ':planning-form-id',
      loadComponent: () =>
        import('./pages/create-update-planning-form-form-page/create-update-planning-form-form-page.component').then(
          m => m.CreateUpdatePlanningFormFormPageComponent
        ),
      canActivate: [crudGuard('planning-form-id')],
      resolve: {
        crud: crudResolver('planning-form-id'),
        breadcrumb: breadcrumbResolver('planning-form-id')
      },
      data: {
        breadcrumbParamValue: 'education.planning_form'
      }
    }
  ]
};

/******************
 * GROUPS
 ******************/
const groups: Route = {
  path: 'groups',
  data: {
    breadcrumb: 'education.groups'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/group-list-page/group-list-page.component').then(
          m => m.GroupListPageComponent
        )
    },
    {
      path: 'detail/:group-id',
      loadComponent: () =>
        import('./pages/group-detail-page/group-detail-page.component').then(
          m => m.GroupDetailPageComponent
        ),
      canActivate: [detailGuard('group-id')],
      resolve: { crud: crudResolver('group-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.group'
      }
    },
    {
      path: ':group-id',
      loadComponent: () =>
        import('./pages/create-update-group-form-page/create-update-group-form-page.component').then(
          m => m.CreateUpdateGroupFormPageComponent
        ),
      canActivate: [crudGuard('group-id')],
      resolve: {
        crud: crudResolver('group-id'),
        breadcrumb: breadcrumbResolver('group-id')
      },
      data: {
        breadcrumbParamValue: 'education.group'
      }
    }
  ]
};

/******************
 * SERVICE HISTORY
 ******************/
const serviceHistories: Route = {
  path: 'service-histories',
  data: {
    breadcrumb: 'shared.service_histories'
  },
  children: [
    {
      path: ':service-history-id',
      loadComponent: () =>
        import('./pages/create-update-service-history-form-page/create-update-service-history-form-page.component').then(
          m => m.CreateUpdateServiceHistoryFormPageComponent
        ),
      canActivate: [crudGuard('service-history-id')],
      resolve: {
        crud: crudResolver('service-history-id'),
        breadcrumb: breadcrumbResolver('service-history-id')
      },
      data: {
        breadcrumbParamValue: 'shared.service_history'
      }
    }
  ]
};

/******************
 * SOLIDARITY CONTRIBUTION BILLS
 ******************/
const solidarityContributionBills: Route = {
  path: 'solidarity-contribution-bills',
  data: {
    breadcrumb: 'shared.solidarity_contribution_bills'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/solidarity-contribution-bill-list-page/solidarity-contribution-bill-list-page.component').then(
          m => m.SolidarityContributionBillListPageComponent
        )
    },
    {
      path: 'detail/:solidarity-contribution-bill-id',
      loadComponent: () =>
        import('./pages/solidarity-contribution-bill-detail-page/solidarity-contribution-bill-detail-page.component').then(
          m => m.SolidarityContributionBillDetailPageComponent
        ),
      canActivate: [detailGuard('solidarity-contribution-bill-id')],
      resolve: { crud: crudResolver('solidarity-contribution-bill-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'shared.solidarity_contribution_bill'
      }
    }
  ]
};

/******************
 * OBSERVATION SHEETS
 ******************/
const observationSheets: Route = {
  path: 'observation-sheets',
  data: {
    breadcrumb: 'education.observation_sheets'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/observation-sheet-list-page/observation-sheet-list-page.component').then(
          m => m.ObservationSheetListPageComponent
        )
    },
    {
      path: 'detail/:observation-sheet-id',
      loadComponent: () =>
        import('./pages/observation-sheet-detail-page/observation-sheet-detail-page.component').then(
          m => m.ObservationSheetDetailPageComponent
        ),
      canActivate: [detailGuard('observation-sheet-id')],
      resolve: { crud: crudResolver('observation-sheet-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.observation_sheet_group'
      }
    },
    {
      path: ':observation-sheet-id',
      loadComponent: () =>
        import('./pages/create-update-observation-sheet-form-page/create-update-observation-sheet-form-page.component').then(
          m => m.CreateUpdateObservationSheetFormPageComponent
        ),
      canActivate: [crudGuard('observation-sheet-id')],
      resolve: {
        crud: crudResolver('observation-sheet-id'),
        breadcrumb: breadcrumbResolver('observation-sheet-id')
      },
      data: {
        breadcrumbParamValue: 'education.observation_sheet'
      }
    }
  ]
};

/******************
 * SERVICE CONTRACTS
 ******************/
const serviceContracts: Route = {
  path: 'service-contracts',
  data: {
    breadcrumb: 'shared.service_contracts'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/service-contract-list-page/service-contract-list-page.component').then(
          m => m.ServiceContractListPageComponent
        )
    },
    {
      path: 'detail/:service-contract-id',
      loadComponent: () =>
        import('~shared/education/pages/service-contract-detail-page/service-contract-detail-page.component').then(
          m => m.ServiceContractDetailPageComponent
        ),
      canActivate: [detailGuard('service-contract-id')],
      resolve: { crud: crudResolver('service-contract-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'shared.service_contract'
      }
    },
    {
      path: ':service-contract-id',
      loadComponent: () =>
        import('./pages/create-service-contract-form-page/create-service-contract-form-page.component').then(
          m => m.CreateServiceContractFormPageComponent
        ),
      canActivate: [ngxPermissionsGuard, crudGuard('service-contract-id', true, false)],
      resolve: {
        crud: crudResolver('service-contract-id'),
        breadcrumb: breadcrumbResolver('service-contract-id')
      },
      data: {
        breadcrumbParamValue: 'shared.service_contract',
        permissions: {
          only: AuthorizationUtil.GROUP_INSPECTOR,
          redirectTo: '/'
        }
      }
    }
  ]
};

/******************
 * CHILD ALLOWANCE APPLICATION
 ******************/
const childAllowanceApplications: Route = {
  path: 'child-allowance-applications',
  data: {
    breadcrumb: 'shared.child_allowance_applications'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/child-allowance-application-list-page/child-allowance-application-list-page.component').then(
          m => m.ChildAllowanceApplicationListPageComponent
        )
    },
    {
      path: 'detail/:child-allowance-application-id',
      loadComponent: () =>
        import('~shared/education/pages/child-allowance-application-detail-page/child-allowance-application-detail-page.component').then(
          m => m.ChildAllowanceApplicationDetailPageComponent
        ),
      canActivate: [detailGuard('child-allowance-application-id')],
      resolve: { crud: crudResolver('child-allowance-application-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'shared.child_allowance_application'
      }
    },
    {
      path: ':child-allowance-application-id',
      loadComponent: () =>
        import('./pages/create-child-allowance-application-form-page/create-child-allowance-application-form-page.component').then(
          m => m.CreateChildAllowanceApplicationFormPageComponent
        ),
      canActivate: [ngxPermissionsGuard, crudGuard('child-allowance-application-id', true, false)],
      resolve: {
        crud: crudResolver('child-allowance-application-id'),
        breadcrumb: breadcrumbResolver('child-allowance-application-id')
      },
      data: {
        breadcrumbParamValue: 'shared.child_allowance_application',
        permissions: {
          only: [...AuthorizationUtil.GROUP_TEACHER_MANAGEMENT, RoleEnum.TEACHER],
          redirectTo: '/'
        }
      }
    }
  ]
};

/******************
 * DECLARATION OF COMMITMENT IL PD
 ******************/
const declarationOfCommitmentILPD: Route = {
  path: 'declaration-of-commitment-il-pds',
  data: {
    breadcrumb: 'shared.declaration_of_commitment_il_pds'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/declaration-of-commitment-il-pd-list-page/declaration-of-commitment-il-pd-list-page.component').then(
          m => m.DeclarationOfCommitmentIlPdListPageComponent
        )
    },
    {
      path: 'detail/:declaration-of-commitment-il-pd-id',
      loadComponent: () =>
        import('~shared/education/pages/declaration-of-commitment-il-pd-detail-page/declaration-of-commitment-il-pd-detail-page.component').then(
          m => m.DeclarationOfCommitmentIlPdDetailPageComponent
        ),
      canActivate: [detailGuard('declaration-of-commitment-il-pd-id')],
      resolve: { crud: crudResolver('declaration-of-commitment-il-pd-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'shared.declaration_of_commitment_il_pd'
      }
    },
    {
      path: ':declaration-of-commitment-il-pd-id',
      loadComponent: () =>
        import('./pages/create-declaration-of-commitment-il-pd-form-page/create-declaration-of-commitment-il-pd-form-page.component').then(
          m => m.CreateDeclarationOfCommitmentIlPdFormPageComponent
        ),
      canActivate: [
        ngxPermissionsGuard,
        crudGuard('declaration-of-commitment-il-pd-id', true, false)
      ],
      resolve: {
        crud: crudResolver('declaration-of-commitment-il-pd-id'),
        breadcrumb: breadcrumbResolver('declaration-of-commitment-il-pd-id')
      },
      data: {
        breadcrumbParamValue: 'shared.declaration_of_commitment_il_pd',
        permissions: {
          only: AuthorizationUtil.GROUP_INSPECTOR,
          redirectTo: '/'
        }
      }
    }
  ]
};

/******************
 * DECLARATION OF COMMITMENT §8 GO
 ******************/
const declarationOfCommitment: Route = {
  path: 'declaration-of-commitments',
  data: {
    breadcrumb: 'education.declaration_of_commitments'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/declaration-of-commitment-list-page/declaration-of-commitment-list-page.component').then(
          m => m.DeclarationOfCommitmentListPageComponent
        )
    },
    {
      path: 'detail/:declaration-of-commitment-id',
      loadComponent: () =>
        import('./pages/declaration-of-commitment-detail-page/declaration-of-commitment-detail-page.component').then(
          m => m.DeclarationOfCommitmentDetailPageComponent
        ),
      canActivate: [detailGuard('declaration-of-commitment-id')],
      resolve: { crud: crudResolver('declaration-of-commitment-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'education.declaration_of_commitment'
      }
    },
    {
      path: ':declaration-of-commitment-id',
      loadComponent: () =>
        import('./pages/create-declaration-of-commitment-form-page/create-declaration-of-commitment-form-page.component').then(
          m => m.CreateDeclarationOfCommitmentFormPageComponent
        ),
      canActivate: [ngxPermissionsGuard, crudGuard('declaration-of-commitment-id', true, false)],
      resolve: {
        crud: crudResolver('declaration-of-commitment-id'),
        breadcrumb: breadcrumbResolver('declaration-of-commitment-id')
      },
      data: {
        breadcrumbParamValue: 'education.declaration_of_commitment',
        permissions: {
          only: AuthorizationUtil.GROUP_INSPECTOR,
          redirectTo: '/'
        }
      }
    }
  ]
};

/******************
 * IGAZA CERTIFICATES
 ******************/
const igazaCertifactes: Route = {
  path: 'igaza-certificates',
  data: {
    breadcrumb: 'shared.igaza_certificates'
  },
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./pages/igaza-certificate-list-page/igaza-certificate-list-page.component').then(
          m => m.IgazaCertificateListPageComponent
        )
    },
    {
      path: 'detail/:igaza-certificate-id',
      loadComponent: () =>
        import('~shared/education/pages/igaza-certificate-detail-page/igaza-certificate-detail-page.component').then(
          m => m.IgazaCertificateDetailPageComponent
        ),
      canActivate: [detailGuard('igaza-certificate-id')],
      resolve: { crud: crudResolver('igaza-certificate-id') },
      data: {
        breadcrumb: 'detail_value',
        breadcrumbParamValue: 'shared.igaza_certificate'
      }
    },
    {
      path: ':igaza-certificate-id',
      loadComponent: () =>
        import('./pages/create-update-igaza-certificate-form-page/create-update-igaza-certificate-form-page.component').then(
          m => m.CreateUpdateIgazaCertificateFormPageComponent
        ),
      canActivate: [ngxPermissionsGuard, crudGuard('igaza-certificate-id', true, false)],
      resolve: {
        crud: crudResolver('igaza-certificate-id'),
        breadcrumb: breadcrumbResolver('igaza-certificate-id')
      },
      data: {
        breadcrumbParamValue: 'shared.igaza_certificate',
        permissions: {
          only: AuthorizationUtil.GROUP_INSPECTOR,
          redirectTo: '/'
        }
      }
    }
  ]
};

/************************************
 * ROUTES
 ************************************/
export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/layout/layout.component').then(m => m.LayoutComponent),
    providers: [provideTranslocoScope('shared'), provideTranslocoScope('education')],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      /******************
       * HOME
       ******************/
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard-page/dashboard-page.component').then(
            m => m.DashboardPageComponent
          ),
        data: {
          breadcrumb: 'dashboard'
        }
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile-page/profile-page.component').then(m => m.ProfilePageComponent),
        data: {
          breadcrumb: 'profile'
        }
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings-page/settings-page.component').then(
            m => m.SettingsPageComponent
          ),
        data: {
          breadcrumb: 'settings'
        }
      },
      /******************
       * MY
       ******************/
      {
        path: 'my',
        canActivate: [ngxPermissionsGuard],
        data: {
          is_personal: true,
          permissions: {
            only: [
              RoleEnum.TEACHER,
              RoleEnum.INSPECTOR,
              RoleEnum.SCHOOL_OFFICE,
              RoleEnum.EDUCATION_OFFICE,
              RoleEnum.EDUCATION_OFFICE_ASSISTANT
            ],
            redirectTo: '/'
          }
        },
        children: [
          ...[absences, solidarityContributionBills].map(route => ({
            ...route,
            canActivate: [...(route.canActivate ?? []), ngxPermissionsGuard],
            data: {
              ...route.data,
              permissions: {
                only: [RoleEnum.TEACHER, RoleEnum.INSPECTOR],
                redirectTo: '/'
              }
            }
          })),
          ...[
            timetables,
            addendumIgazas,
            partTimeJobs,
            transferServices,
            groups,
            planningForms,
            letterOfServices,
            observationSheets,
            serviceContracts,
            childAllowanceApplications,
            declarationOfCommitmentILPD,
            declarationOfCommitment
          ].map(route => ({
            ...route,
            canActivate: [...(route.canActivate ?? []), ngxPermissionsGuard],
            data: {
              ...route.data,
              permissions: {
                only: [RoleEnum.TEACHER],
                redirectTo: '/'
              }
            }
          })),
          hearingScoring
        ]
      },
      /******************
       * ROUTES
       ******************/
      ...[
        timetables,
        absences,
        addendumIgazas,
        partTimeJobs,
        transferServices,
        planningForms,
        letterOfServices,
        terminationOfServices,
        proposalHonors,
        groups,
        serviceHistories,
        observationSheets,
        serviceContracts,
        childAllowanceApplications,
        declarationOfCommitmentILPD,
        declarationOfCommitment,
        igazaCertifactes,
        hearings,
        hearingSessions
      ].map(route => ({
        ...route,
        canActivate: [ngxPermissionsGuard],
        data: {
          ...route.data,
          permissions: {
            only: AuthorizationUtil.GROUP_INSPECTOR,
            redirectTo: '/'
          }
        }
      })),
      ...[solidarityContributionBills].map(route => ({
        ...route,
        canActivate: [ngxPermissionsGuard],
        data: {
          ...route.data,
          permissions: {
            only: AuthorizationUtil.GROUP_EDUCATION,
            redirectTo: '/'
          }
        }
      })),
      /******************
       * DOCUMENTS
       ******************/
      {
        path: 'documents',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./pages/document-list-page/document-list-page.component').then(
            m => m.DocumentListPageComponent
          ),
        data: {
          breadcrumb: 'documents',
          permissions: {
            only: AuthorizationUtil.GROUP_INSPECTOR,
            redirectTo: '/'
          }
        }
      },
      /******************
       * SCHOOLS
       ******************/
      {
        path: 'schools',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'education.schools',
          permissions: {
            only: AuthorizationUtil.GROUP_INSPECTOR,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/school-list-page/school-list-page.component').then(
                m => m.SchoolListPageComponent
              ),
            data: {}
          },
          {
            path: 'detail/:school-id',
            loadComponent: () =>
              import('./pages/school-detail-page/school-detail-page.component').then(
                m => m.SchoolDetailPageComponent
              ),
            canActivate: [detailGuard('school-id')],
            resolve: { crud: crudResolver('school-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'education.school'
            }
          },
          {
            path: 'scrape',
            loadComponent: () =>
              import('./pages/scrape-schools-page/import-scrape-schools-page.component').then(
                m => m.ImportScrapeSchoolsPageComponent
              ),
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'education.scrape_schools',
              permissions: {
                only: AuthorizationUtil.GROUP_EDUCATION,
                redirectTo: '/'
              }
            }
          },
          {
            path: 'bev-import',
            loadComponent: () =>
              import('./pages/bev-import-page/bev-import-page.component').then(
                m => m.BevImportPageComponent
              ),
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'education.bev_import',
              permissions: {
                only: AuthorizationUtil.GROUP_EDUCATION,
                redirectTo: '/'
              }
            }
          },
          {
            path: 'deployment-planning',
            loadComponent: () =>
              import('./pages/deployment-planning-page/deployment-planning-page.component').then(
                m => m.DeploymentPlanningPageComponent
              ),
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'education.deployment_planning',
              permissions: {
                only: AuthorizationUtil.GROUP_INSPECTOR,
                redirectTo: '/'
              }
            }
          }
        ]
      },
      /******************
       * PRINTS
       ******************/
      {
        path: 'print',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./pages/create-print-form-page/create-print-form-page.component').then(
            m => m.CreatePrintFormPageComponent
          ),
        data: {
          breadcrumb: 'print',
          permissions: {
            only: AuthorizationUtil.GROUP_INSPECTOR,
            redirectTo: '/'
          }
        }
      },
      {
        path: 'print-history',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./pages/print-list-page/print-list-page.component').then(
            m => m.PrintListPageComponent
          ),
        data: {
          breadcrumb: 'shared.print_history',
          permissions: {
            only: AuthorizationUtil.GROUP_INSPECTOR,
            redirectTo: '/'
          }
        }
      },
      {
        path: 'print-templates',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.print_templates',
          permissions: {
            only: AuthorizationUtil.GROUP_EDUCATION,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('~shared/print/pages/print-management-page/print-management-page.component').then(
                m => m.PrintManagementPageComponent
              )
          },
          {
            path: ':print-template-id',
            loadComponent: () =>
              import('~shared/print/pages/create-update-print-template-form-page/create-update-print-template-form-page.component').then(
                m => m.CreateUpdatePrintTemplateFormPageComponent
              ),
            canActivate: [crudGuard('print-template-id')],
            resolve: {
              crud: crudResolver('print-template-id'),
              breadcrumb: breadcrumbResolver('print-template-id')
            },
            data: {
              breadcrumbParamValue: 'shared.print_template'
            }
          }
        ]
      },
      /******************
       * MAIL TEMPLATES
       ******************/
      {
        path: 'mail-templates',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.mail_templates',
          permissions: {
            only: AuthorizationUtil.GROUP_EDUCATION,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('~shared/mail/pages/mail-template-list-page/mail-template-list-page.component').then(
                m => m.MailTemplateListPageComponent
              )
          },
          {
            path: 'detail/:mail-template-id',
            loadComponent: () =>
              import('~shared/mail/pages/mail-template-detail-page/mail-template-detail-page.component').then(
                m => m.MailTemplateDetailPageComponent
              ),
            canActivate: [detailGuard('mail-template-id')],
            resolve: { crud: crudResolver('mail-template-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.mail_template'
            }
          },
          {
            path: 'detail/data/:mail-template-data-id',
            loadComponent: () =>
              import('~shared/mail/pages/mail-template-detail-page/mail-template-detail-page.component').then(
                m => m.MailTemplateDetailPageComponent
              ),
            canActivate: [detailGuard('mail-template-data-id')],
            resolve: { crud: crudResolver('mail-template-data-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.mail_template',
              isMailTemplateData: true
            }
          },
          {
            path: ':mail-template-id',
            loadComponent: () =>
              import('~shared/mail/pages/create-update-mail-template-form-page/create-update-mail-template-form-page.component').then(
                m => m.CreateUpdateMailTemplateFormPageComponent
              ),
            canActivate: [crudGuard('mail-template-id')],
            resolve: {
              crud: crudResolver('mail-template-id'),
              breadcrumb: breadcrumbResolver('mail-template-id')
            },
            data: {
              breadcrumbParamValue: 'shared.mail_template'
            }
          }
        ]
      },
      /******************
       * STATISTICS
       ******************/
      {
        path: 'statistics',
        loadComponent: () =>
          import('./pages/statistics-page/statistics-page.component').then(
            m => m.StatisticsPageComponent
          ),
        data: {
          breadcrumb: 'shared.statistics'
        }
      },
      /******************
       * ANNOUNCEMENTS
       ******************/
      {
        path: 'announcements',
        data: {
          breadcrumb: 'shared.announcements'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/announcement-list-page/announcement-list-page.component').then(
                m => m.AnnouncementListPageComponent
              ),
            canActivate: [ngxPermissionsGuard],
            data: {
              permissions: {
                only: AuthorizationUtil.GROUP_INSPECTOR,
                redirectTo: '/'
              }
            }
          },
          {
            path: 'chronicles',
            loadComponent: () =>
              import('./pages/announcement-chronicle-list-page/announcement-chronicle-list-page.component').then(
                m => m.AnnouncementChronicleListPageComponent
              ),
            data: {
              breadcrumb: 'shared.chronicle'
            }
          },
          {
            path: 'detail/:announcement-id',
            loadComponent: () =>
              import('./pages/announcement-detail-page/announcement-detail-page.component').then(
                m => m.AnnouncementDetailPageComponent
              ),
            canActivate: [ngxPermissionsGuard, detailGuard('announcement-id')],
            resolve: { crud: crudResolver('announcement-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.announcement',
              permissions: {
                only: AuthorizationUtil.GROUP_INSPECTOR,
                redirectTo: '/'
              }
            }
          },
          {
            path: ':announcement-id',
            loadComponent: () =>
              import('./pages/create-update-announcement-form-page/create-update-announcement-form-page.component').then(
                m => m.CreateUpdateAnnouncementFormPageComponent
              ),
            canActivate: [ngxPermissionsGuard, crudGuard('announcement-id')],
            resolve: {
              crud: crudResolver('announcement-id'),
              breadcrumb: breadcrumbResolver('announcement-id')
            },
            data: {
              breadcrumbParamValue: 'shared.announcement',
              permissions: {
                only: AuthorizationUtil.GROUP_INSPECTOR,
                redirectTo: '/'
              }
            }
          }
        ]
      },
      /******************
       * NOTIFICATIONS
       ******************/
      {
        path: 'messages',
        loadComponent: () =>
          import('~shared/notification/pages/notification-page/notification-page.component').then(
            m => m.NotificationPageComponent
          ),
        data: {
          roles: [],
          breadcrumb: 'shared.messages'
        },
        children: [
          {
            path: '',
            redirectTo: 'inbox',
            pathMatch: 'full'
          },
          {
            path: 'new',
            loadComponent: () =>
              import('~shared/notification/components/create-thread-form/create-thread-form.component').then(
                m => m.CreateThreadFormComponent
              ),
            resolve: { notification: notificationResolver() },
            data: {
              is_new: true,
              breadcrumb: 'add_value',
              breadcrumbParamValue: 'shared.message'
            }
          },
          {
            path: ':folder-id',
            loadComponent: () =>
              import('~shared/notification/components/thread-list/thread-list.component').then(
                m => m.ThreadListComponent
              ),
            resolve: { breadcrumb: folderNameBreadcrumbResolver('folder-id') },
            children: [
              {
                path: ':thread-id',
                loadComponent: () =>
                  import('~shared/notification/components/thread/thread.component').then(
                    m => m.ThreadComponent
                  ),
                canActivate: [crudGuard('thread-id', false, true)],
                resolve: { crud: crudResolver('thread-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.message'
                }
              }
            ]
          }
        ]
      },
      /******************
       * EVENTS
       ******************/
      {
        path: 'events',
        data: {
          breadcrumb: 'shared.events'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/event-list-page/event-list-page.component').then(
                m => m.EventListPageComponent
              )
          },
          {
            path: 'detail/:event-id',
            loadComponent: () =>
              import('./pages/event-detail-page/event-detail-page.component').then(
                m => m.EventDetailPageComponent
              ),
            canActivate: [detailGuard('event-id')],
            resolve: { crud: crudResolver('event-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.event'
            }
          },
          {
            path: ':event-id',
            loadComponent: () =>
              import('./pages/create-update-event-form-page/create-update-event-form-page.component').then(
                m => m.CreateUpdateEventFormPageComponent
              ),
            canActivate: [ngxPermissionsGuard, crudGuard('event-id')],
            resolve: {
              crud: crudResolver('event-id'),
              breadcrumb: breadcrumbResolver('event-id')
            },
            data: {
              breadcrumbParamValue: 'shared.event',
              permissions: {
                only: AuthorizationUtil.GROUP_INSPECTOR,
                redirectTo: '/'
              }
            }
          }
        ]
      },
      /******************
       * ACTORS
       ******************/
      {
        path: 'actors',
        data: {
          breadcrumb: 'shared.actors'
        },
        children: [
          {
            path: 'school-enrolment-applications',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'shared.school_enrolment_title',
              permissions: {
                only: AuthorizationUtil.GROUP_INSPECTOR,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('~shared/shared/pages/application-list-page/application-list-page.component').then(
                    m => m.ApplicationListPageComponent
                  ),
                data: {
                  variant: ApplicationListVariantEnum.SCHOOL_ENROLMENT_READONLY,
                  isReadOnly: true
                }
              },
              {
                path: 'detail/:id',
                loadComponent: () =>
                  import('~shared/shared/pages/school-enrolment-application-detail-page/school-enrolment-application-detail-page.component').then(
                    m => m.SchoolEnrolmentApplicationDetailPageComponent
                  ),
                data: {
                  table: 'external_school_enrolment_overview',
                  isReadOnly: true,
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.school_enrolment_title'
                }
              }
            ]
          },
          {
            path: 'people',
            data: {
              breadcrumb: 'shared.people'
            },
            children: [
              {
                path: '',
                canActivate: [ngxPermissionsGuard],
                loadComponent: () =>
                  import('./pages/person-list-page/person-list-page.component').then(
                    m => m.PersonListPageComponent
                  ),
                data: {
                  permissions: {
                    only: AuthorizationUtil.GROUP_EDUCATION,
                    redirectTo: '/'
                  }
                }
              },
              {
                path: 'detail/:person-id',
                loadComponent: () =>
                  import('./pages/person-detail-page/person-detail-page.component').then(
                    m => m.PersonDetailPageComponent
                  ),
                canActivate: [
                  ngxPermissionsGuard,
                  detailGuard('person-id'),
                  CanAccessPersonDetailGuard
                ],
                resolve: { crud: crudResolver('person-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.person',
                  permissions: {
                    only: AuthorizationUtil.GROUP_INSPECTOR,
                    redirectTo: '/'
                  }
                }
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('~shared/core/pages/create-person-data-form-page/create-person-data-form-page.component').then(
                    m => m.CreatePersonDataFormPageComponent
                  ),
                canActivate: [ngxPermissionsGuard, crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.person',
                  permissions: {
                    only: AuthorizationUtil.GROUP_EDUCATION,
                    redirectTo: '/'
                  }
                }
              }
            ]
          },
          {
            path: 'teachers',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'shared.role.teachers',
              permissions: {
                only: AuthorizationUtil.GROUP_INSPECTOR,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/teacher-list-page/teacher-list-page.component').then(
                    m => m.TeacherListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                redirectTo: '/actors/people/detail/:person-id'
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('./pages/create-teacher-data-form-page/create-teacher-data-form-page.component').then(
                    m => m.CreateTeacherDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id'), CanAccessTeacherFormGuard],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.teacher'
                }
              }
            ]
          },
          {
            path: 'inspectors',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'shared.role.inspectors',
              permissions: {
                only: AuthorizationUtil.GROUP_SCHOOL,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/inspector-list-page/inspector-list-page.component').then(
                    m => m.InspectorListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                redirectTo: '/actors/people/detail/:person-id'
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('./pages/create-inspector-data-form-page/create-inspector-data-form-page.component').then(
                    m => m.CreateInspectorDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.inspector'
                }
              }
            ]
          },
          {
            path: 'school-offices',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'shared.role.school_offices',
              permissions: {
                only: AuthorizationUtil.GROUP_EDUCATION,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/school-office-list-page/school-office-list-page.component').then(
                    m => m.SchoolOfficeListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                redirectTo: '/actors/people/detail/:person-id'
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('./pages/create-school-office-data-form-page/create-school-office-data-form-page.component').then(
                    m => m.CreateSchoolOfficeDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.school_office'
                }
              }
            ]
          },
          {
            path: 'education-office-assistants',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'shared.role.education_office_assistants',
              permissions: {
                only: AuthorizationUtil.GROUP_EDUCATION_WITHOUT_ASSISTANT,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/education-office-assistant-list-page/education-office-assistant-list-page.component').then(
                    m => m.EducationOfficeAssistantListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                redirectTo: '/actors/people/detail/:person-id'
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('./pages/create-education-office-assistant-data-form-page/create-education-office-assistant-data-form-page.component').then(
                    m => m.CreateEducationOfficeAssistantDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.education_office_assistant'
                }
              }
            ]
          },
          {
            path: 'education-offices',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'shared.role.education_offices',
              permissions: {
                only: [RoleEnum.MODERATOR],
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/education-office-list-page/education-office-list-page.component').then(
                    m => m.EducationOfficeListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                redirectTo: '/actors/people/detail/:person-id'
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('./pages/create-education-office-data-form-page/create-education-office-data-form-page.component').then(
                    m => m.CreateEducationOfficeDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.education_office'
                }
              }
            ]
          },
          {
            path: 'education-directorate-staff',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'education.education_directorate_staffs',
              permissions: {
                only: AuthorizationUtil.GROUP_INSPECTOR,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: 'detail/:person-id',
                redirectTo: '/actors/people/detail/:person-id'
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('./pages/create-education-directorate-staff-data-form-page/create-education-directorate-staff-data-form-page.component').then(
                    m => m.CreateEducationDirectorateStaffDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'education.education_directorate_staff'
                }
              }
            ]
          }
        ]
      },
      /******************
       * ADMINISTRATION
       ******************/
      {
        path: 'administration',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.administration',
          permissions: {
            only: AuthorizationUtil.GROUP_INSPECTOR,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'education',
            data: {
              breadcrumb: 'shared.education'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/education-administration-page/education-administration-page.component').then(
                    m => m.EducationAdministrationPageComponent
                  )
              },
              {
                path: 'education-directorates',
                data: {
                  breadcrumb: 'shared.education_directorates'
                },
                children: [
                  {
                    path: 'detail/:education-directorate-id',
                    loadComponent: () =>
                      import('./pages/education-directorate-detail-page/education-directorate-detail-page.component').then(
                        m => m.EducationDirectorateDetailPageComponent
                      ),
                    canActivate: [detailGuard('education-directorate-id')],
                    resolve: { crud: crudResolver('education-directorate-id') },
                    data: {
                      breadcrumb: 'detail_value',
                      breadcrumbParamValue: 'shared.education_directorate'
                    }
                  },
                  {
                    path: ':education-directorate-id',
                    loadComponent: () =>
                      import('./pages/create-education-directorate-data-form-page/create-education-directorate-data-form-page.component').then(
                        m => m.CreateEducationDirectorateDataFormPageComponent
                      ),
                    canActivate: [ngxPermissionsGuard, crudGuard('education-directorate-id')],
                    resolve: {
                      crud: crudResolver('education-directorate-id'),
                      breadcrumb: breadcrumbResolver('education-directorate-id')
                    },
                    data: {
                      breadcrumbParamValue: 'shared.education_directorate',
                      permissions: {
                        only: AuthorizationUtil.GROUP_EDUCATION,
                        redirectTo: '/'
                      }
                    }
                  }
                ]
              },
              {
                path: 'observation-sheets',
                data: {
                  breadcrumb: 'education.observation_sheets'
                },
                children: [
                  {
                    path: 'groups',
                    data: {
                      breadcrumb: 'education.observation_sheet_groups'
                    },
                    children: [
                      {
                        path: 'detail/:observation-sheet-group-id',
                        loadComponent: () =>
                          import('./pages/observation-sheet-group-detail-page/observation-sheet-group-detail-page.component').then(
                            m => m.ObservationSheetGroupDetailPageComponent
                          ),
                        canActivate: [detailGuard('observation-sheet-group-id')],
                        resolve: { crud: crudResolver('observation-sheet-group-id') },
                        data: {
                          breadcrumb: 'detail_value',
                          breadcrumbParamValue: 'education.observation_sheet_group'
                        }
                      },
                      {
                        path: ':observation-sheet-group-id',
                        loadComponent: () =>
                          import('./pages/create-update-observation-sheet-group-form-page/create-update-observation-sheet-group-form-page.component').then(
                            m => m.CreateUpdateObservationSheetGroupFormPageComponent
                          ),
                        canActivate: [ngxPermissionsGuard, crudGuard('observation-sheet-group-id')],
                        resolve: {
                          crud: crudResolver('observation-sheet-group-id'),
                          breadcrumb: breadcrumbResolver('observation-sheet-group-id')
                        },
                        data: {
                          breadcrumbParamValue: 'education.observation_sheet_group',
                          permissions: {
                            only: AuthorizationUtil.GROUP_EDUCATION,
                            redirectTo: '/'
                          }
                        }
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            path: 'actors',
            canActivate: [ngxPermissionsGuard],
            data: {
              breadcrumb: 'shared.actors',
              permissions: {
                only: AuthorizationUtil.GROUP_SCHOOL,
                redirectTo: '/'
              }
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/actor-administration-page/actor-administration-page.component').then(
                    m => m.ActorAdministrationPageComponent
                  )
              },
              {
                path: 'teacher-status',
                data: {
                  breadcrumb: 'education.teacher_status'
                },
                children: [
                  {
                    path: ':teacher-status-id',
                    loadComponent: () =>
                      import('./pages/create-update-teacher-status-form-page/create-update-teacher-status-form-page.component').then(
                        m => m.CreateUpdateTeacherStatusFormPageComponent
                      ),
                    canActivate: [crudGuard('teacher-status-id')],
                    resolve: {
                      crud: crudResolver('teacher-status-id'),
                      breadcrumb: breadcrumbResolver('teacher-status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'education.teacher_status'
                    }
                  }
                ]
              },
              {
                path: 'inspector-status',
                data: {
                  breadcrumb: 'education.inspector_status'
                },
                children: [
                  {
                    path: ':inspector-status-id',
                    loadComponent: () =>
                      import('./pages/create-update-inspector-status-form-page/create-update-inspector-status-form-page.component').then(
                        m => m.CreateUpdateInspectorStatusFormPageComponent
                      ),
                    canActivate: [ngxPermissionsGuard, crudGuard('inspector-status-id')],
                    resolve: {
                      crud: crudResolver('inspector-status-id'),
                      breadcrumb: breadcrumbResolver('inspector-status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'education.inspector_status',
                      permissions: {
                        only: AuthorizationUtil.GROUP_EDUCATION,
                        redirectTo: '/'
                      }
                    }
                  }
                ]
              },
              {
                path: 'school-office-status',
                data: {
                  breadcrumb: 'education.school_office_status'
                },
                children: [
                  {
                    path: ':school-office-status-id',
                    loadComponent: () =>
                      import('./pages/create-update-school-office-status-form-page/create-update-school-office-status-form-page.component').then(
                        m => m.CreateUpdateSchoolOfficeStatusFormPageComponent
                      ),
                    canActivate: [ngxPermissionsGuard, crudGuard('school-office-status-id')],
                    resolve: {
                      crud: crudResolver('school-office-status-id'),
                      breadcrumb: breadcrumbResolver('school-office-status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'education.school_office_status',
                      permissions: {
                        only: AuthorizationUtil.GROUP_EDUCATION,
                        redirectTo: '/'
                      }
                    }
                  }
                ]
              },
              {
                path: 'education-office-assistant-status',
                data: {
                  breadcrumb: 'education.education_office_assistant_status'
                },
                children: [
                  {
                    path: ':education-office-assistant-status-id',
                    loadComponent: () =>
                      import('./pages/create-update-education-office-assistant-status-form-page/create-update-education-office-assistant-status-form-page.component').then(
                        m => m.CreateUpdateEducationOfficeAssistantStatusFormPageComponent
                      ),
                    canActivate: [
                      ngxPermissionsGuard,
                      crudGuard('education-office-assistant-status-id')
                    ],
                    resolve: {
                      crud: crudResolver('education-office-assistant-status-id'),
                      breadcrumb: breadcrumbResolver('education-office-assistant-status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'education.education_office_assistant_status',
                      permissions: {
                        only: AuthorizationUtil.GROUP_EDUCATION_WITHOUT_ASSISTANT,
                        redirectTo: '/'
                      }
                    }
                  }
                ]
              },
              {
                path: 'education-office-status',
                data: {
                  breadcrumb: 'education.education_office_status'
                },
                children: [
                  {
                    path: ':education-office-status-id',
                    loadComponent: () =>
                      import('./pages/create-update-education-office-status-form-page/create-update-education-office-status-form-page.component').then(
                        m => m.CreateUpdateEducationOfficeStatusFormPageComponent
                      ),
                    canActivate: [ngxPermissionsGuard, crudGuard('education-office-status-id')],
                    resolve: {
                      crud: crudResolver('education-office-status-id'),
                      breadcrumb: breadcrumbResolver('education-office-status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'education.education_office_status',
                      permissions: {
                        only: RoleEnum.MODERATOR,
                        redirectTo: '/'
                      }
                    }
                  }
                ]
              }
            ]
          }
        ]
      },
      /******************
       * NOT FOUND
       ******************/
      {
        path: 'not-found',
        loadComponent: () =>
          import('~shared/shared/pages/page-not-found-page/page-not-found-page.component').then(
            m => m.PageNotFoundPageComponent
          ),
        data: {
          breadcrumb: 'page_not_found'
        }
      },
      {
        path: 'logout',
        canActivate: [logoutGuard()],
        loadComponent: () =>
          import('~shared/shared/pages/page-not-found-page/page-not-found-page.component').then(
            m => m.PageNotFoundPageComponent
          )
      },
      { path: '**', redirectTo: 'not-found' }
    ]
  }
];
